import React, { useState } from 'react';
import { Box, Container, Typography, TextField, Button, Alert, CircularProgress } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';

function friendlyError(msg = '') {
  if (msg.includes('Password should be')) return 'Password must be at least 6 characters.';
  if (msg.toLowerCase().includes('different from the old')) return 'Choose a different password than your current one.';
  return msg || 'Something went wrong. Please try again.';
}

/**
 * Landing page for the link Supabase emails from resetPasswordForEmail().
 * detectSessionInUrl (already on in supabase.js) parses the recovery token
 * from the URL and establishes a real session before this page even
 * renders meaningfully — AuthContext's existing session bootstrap (with
 * its own hard-refresh/lock-contention handling) picks that up the same
 * way it would any other session, so this page just reads currentUser/
 * authLoading from context rather than re-implementing session detection.
 *
 * A valid link -> currentUser becomes non-null once authLoading settles,
 * and updateUser({ password }) works against that session exactly like
 * the logged-in Account view's password change. An invalid/expired link
 * never produces a session, so currentUser stays null once authLoading
 * settles — that's the one signal this page needs to tell the two cases
 * apart.
 */
export default function ResetPasswordPage() {
  const { currentUser, authLoading } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (password.length < 6) { setError('Password must be at least 6 characters.'); return; }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setSaving(true);
    try {
      const { error: err } = await supabase.auth.updateUser({ password });
      if (err) throw err;
      setDone(true);
    } catch (err) {
      setError(friendlyError(err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh' }}>
      <Navbar />
      <Container maxWidth="sm" sx={{ py: 10 }}>
        {authLoading ? (
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress sx={{ color: '#2ea84b' }} />
          </Box>
        ) : done ? (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Password updated</Typography>
            <Typography sx={{ color: '#9eb8d0', mb: 3 }}>
              You can now use your new password to sign in.
            </Typography>
            <Button variant="contained" color="primary" onClick={() => navigate('/')} sx={{ textTransform: 'none', fontWeight: 700 }}>
              Continue to Football for Nerds →
            </Button>
          </Box>
        ) : !currentUser ? (
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Reset link invalid or expired</Typography>
            <Typography sx={{ color: '#9eb8d0', mb: 3 }}>
              Request a new password reset link from the Sign In screen.
            </Typography>
            <Button variant="contained" color="primary" onClick={() => navigate('/')} sx={{ textTransform: 'none', fontWeight: 700 }}>
              Back to Football for Nerds
            </Button>
          </Box>
        ) : (
          <Box component="form" onSubmit={handleSubmit}>
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>Set a new password</Typography>
            <Typography sx={{ color: '#9eb8d0', mb: 3 }}>{currentUser.email}</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            <TextField
              type="password" label="New Password" fullWidth value={password} onChange={e => setPassword(e.target.value)}
              sx={{ mb: 2 }} inputProps={{ minLength: 6 }} autoComplete="new-password" required
            />
            <TextField
              type="password" label="Confirm Password" fullWidth value={confirm} onChange={e => setConfirm(e.target.value)}
              sx={{ mb: 2 }} inputProps={{ minLength: 6 }} autoComplete="new-password" required
            />
            <Button type="submit" variant="contained" color="primary" fullWidth disabled={saving}
              sx={{ textTransform: 'none', fontWeight: 700, py: 1.1 }}>
              {saving ? <CircularProgress size={20} color="inherit" /> : 'Update Password'}
            </Button>
          </Box>
        )}
      </Container>
    </Box>
  );
}
